import { authorizedFetch, parseApiErrorMessage } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'


export type CanvasTypeId = 'business_model' | 'lean' | 'strategy'

export type CanvasBlockSpec = {
  id: string
  label: string
  hint: string
}

export type CanvasTemplate = {
  label: string
  description: string
  layout: 'bmc' | 'stub'
  stub: boolean
  blocks: CanvasBlockSpec[]
}

export type CanvasListItem = {
  id: string
  title: string
  canvas_type: CanvasTypeId
  type_label: string
  stub: boolean
  updated_at: string
  created_at: string
}

export type WorkshopCanvas = {
  id: string
  title: string
  canvas_type: CanvasTypeId
  type_label: string
  blocks: Record<string, string[]>
  template: CanvasTemplate
  updated_at: string
  created_at: string
}

async function fetchCanvases(): Promise<CanvasListItem[]> {
  const response = await authorizedFetch('/api/canvases/')
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Could not load canvases'))
  }
  return (await response.json()) as CanvasListItem[]
}

async function fetchCanvas(id: string): Promise<WorkshopCanvas> {
  const response = await authorizedFetch(`/api/canvases/${id}`)
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Canvas not found'))
  }
  return (await response.json()) as WorkshopCanvas
}

export function useCanvases(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['canvases'],
    queryFn: fetchCanvases,
    enabled: options?.enabled !== false,
  })
}

export function useWorkshopCanvas(canvasId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['workshop-canvas', canvasId],
    queryFn: () => fetchCanvas(canvasId!),
    enabled: Boolean(canvasId) && options?.enabled !== false,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateCanvas() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { title?: string; canvas_type: CanvasTypeId }) => {
      const response = await authorizedFetch('/api/canvases/', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not create canvas'))
      }
      return (await response.json()) as WorkshopCanvas
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canvases'] })
    },
  })
}

export function useUpdateCanvas(canvasId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { title?: string; blocks?: Record<string, string[]> }) => {
      const response = await authorizedFetch(`/api/canvases/${canvasId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not save canvas'))
      }
      return (await response.json()) as WorkshopCanvas
    },
    onSuccess: (canvas) => {
      queryClient.setQueryData(['workshop-canvas', canvas.id], canvas)
      void queryClient.invalidateQueries({ queryKey: ['canvases'] })
    },
  })
}

export function useDeleteCanvas() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (canvasId: string) => {
      const response = await authorizedFetch(`/api/canvases/${canvasId}`, { method: 'DELETE' })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not delete canvas'))
      }
    },
    onSuccess: (_void, canvasId) => {
      queryClient.removeQueries({ queryKey: ['workshop-canvas', canvasId] })
      void queryClient.invalidateQueries({ queryKey: ['canvases'] })
    },
  })
}
